import { motion } from "framer-motion";

export default function EtAboutHero() {

  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose — High-energy intro with controlled motion
  // Updated — 23 Nov 2025 - Copy refinement


  return (
    <section className="relative overflow-hidden py-40 bg-black text-white">

      {/* --- 1. Background Gradient Layer --- */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-orange-900/40"></div>

      {/* --- 2. Texture Overlay (subtle cubic pattern) --- */}
      <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

      {/* --- 3. Radiant Energy Ring (Orange Pulse) --- */}
      <div
        className="
          absolute top-1/2 left-1/2 w-[900px] h-[900px]
          -translate-x-1/2 -translate-y-1/2
          rounded-full
          bg-orange-500/10 blur-[120px]
          animate-pulse-slow
        "
      ></div>

      {/* --- 4. Vertical Trinity Beam Lines --- */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[2px] h-full bg-gradient-to-b from-orange-600/40 via-orange-700/20 to-transparent animate-beam-float"></div>
        <div className="absolute top-0 left-1/2 w-[2px] h-full bg-gradient-to-b from-orange-600/30 via-orange-700/10 to-transparent animate-beam-float-delayed"></div>
        <div className="absolute top-0 left-3/4 w-[2px] h-full bg-gradient-to-b from-orange-600/20 via-orange-700/10 to-transparent animate-beam-float-slow"></div>
      </div>

      {/* --- 5. Ambient Particle Field --- */}
      <div className="pointer-events-none absolute inset-0">
        {[...Array(200)].map((_, i) => (
          <div
            key={i}
            className={`
              absolute w-1 h-1 bg-orange-300/40 rounded-full 
              animate-particle-float
            `}
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 6}s`,
              animationDuration: `${6 + Math.random() * 6}s`,
            }}
          ></div>
        ))}
      </div>

      {/* --- 6. Main Hero Copy --- */}
      <motion.div
        className="relative z-20 max-w-5xl mx-auto text-center px-6"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
      >
        <motion.h1
          className="text-4xl md:text-6xl font-bold text-orange-500 drop-shadow-xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1.2 }}
        >
          The Story Behind Eternity & Trinity
        </motion.h1>

        <motion.p
          className="mt-6 text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1.2 }}
        >
          {/* A purpose-native journey for leaders who want to think better,
          execute sharper, and grow with clarity. */}
          {/* 23 Nov 2025 Additions */}
          {/* Where purpose meets performance. Where leaders learn to think deeper. To act sharper. To grow with intent. */}
          {/* A clarity-driven blueprint for leaders. To align purpose. To sharpen execution. To accelerate meaningful growth. */}
          {/* A journey into purpose and clarity. A path defined by daily discipline. The kind that shapes trusted leaders and resilient organizations. */}
          Built for leaders who want more than growth. For those who seek <span className="text-orange-600">clarity</span> of <span className="text-orange-600">purpose</span>. Strength of <span className="text-orange-600">execution</span>. And a <span className="text-orange-600">rhythm</span> that sustains both.
        </motion.p>
      </motion.div>

      {/* --- Ambient Scroll Cue (Optional) --- */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-gray-400 text-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        transition={{ delay: 1.2, duration: 1.2 }}
      >
        ↓ Scroll to begin the journey
      </motion.div>

      {/* --- Motion Keyframes --- */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.2; transform: scale(0.95); }
          50% { opacity: 0.45; transform: scale(1.05); }
        }

        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }

        @keyframes float {
          0% { transform: translateY(0px); opacity: 0.4; }
          50% { transform: translateY(-40px); opacity: 0.7; }
          100% { transform: translateY(0px); opacity: 0.4; }
        }

        .animate-beam-float { animation: float 6s ease-in-out infinite; }
        .animate-beam-float-delayed { animation: float 7.5s ease-in-out infinite; }
        .animate-beam-float-slow { animation: float 9s ease-in-out infinite; }

        @keyframes particle-float {
          0% { transform: translateY(0) scale(0.7); opacity: 0.2; }
          50% { transform: translateY(-30px) scale(1); opacity: 0.6; }
          100% { transform: translateY(0) scale(0.7); opacity: 0.2; }
        }

        .animate-particle-float { animation: particle-float linear infinite; }
      `}</style>
    </section>
  );
}